import { isDuration } from "src/utils/date";
import { SearchState, SearchAction, SearchActions } from "../types";
import { askEntityExtraction } from "src/utils/strings";

export function createStateReducer(
  merge: (reduced: SearchState) => SearchState = (x) => x
): (state: SearchState, action: SearchAction) => SearchState {
  return (state, action) => merge(reduce(state, action));

  function reduce(state: SearchState, action: SearchAction): SearchState {
    switch (action.type) {
      case SearchActions.setSearch: {
        return { ...state, search: action.value };
      }
      case SearchActions.setAggregations: {
        return {
          ...state,
          aggregations: action.value,
          compareWith: undefined,
        };
      }
      case SearchActions.setHaving: {
        return {
          ...state,
          having: action.value,
        };
      }
      case SearchActions.setGroupBys: {
        const newGroupBys =
          ((action.value?.length || 0) === 0 ? [] : action.value) || [];

        return { ...state, groupBys: newGroupBys, compareWith: undefined };
      }
      case SearchActions.setDates: {
        return {
          ...state,
          duration: undefined,
          startDate: action.value[0],
          endDate: action.value[1],
          compareWith: undefined,
        };
      }
      case SearchActions.setDuration: {
        return {
          ...state,
          duration: action.value,
          startDate: undefined,
          endDate: undefined,
        };
      }
      case SearchActions.setCompareWith: {
        return { ...state, compareWith: action.value };
      }
      case SearchActions.setFilters: {
        return { ...state, filters: action.value };
      }
      case SearchActions.setSelection: {
        return {
          ...state,
          select: action.value || [],
          compareWith: undefined,
        };
      }
      case SearchActions.resetDate: {
        return {
          ...state,
          duration: undefined,
          startDate: undefined,
          endDate: undefined,
          compareWith: undefined,
        };
      }
      case SearchActions.reset: {
        return {
          ...state,
          aggregations: [],
          groupBys: [],
          filters: [],
          select: [],
          search: "",
          duration: undefined,
          startDate: undefined,
          endDate: undefined,
          compareWith: undefined,
        };
      }
      case SearchActions.loadState: {
        return {
          ...state,
          aggregations: [],
          groupBys: [],
          filters: [],
          select: [],
          formulas: [],
          having: [],
          search: "",
          duration: undefined,
          startDate: undefined,
          endDate: undefined,
          compareWith: undefined,
          ...unserializeParams(action.value),
        };
      }
      default: {
        throw new Error("unknown action type");
      }
    }
  }
}

export function serializeParams(data: any) {
  const detyped = [..."unescape"].join("");
  const unescape = window[detyped as keyof typeof window] as (
    val: string // AlL tHiS tO gET TS To ShUT uP aBoUt `window.unescape`
  ) => string;
  return window.btoa(unescape(encodeURIComponent(JSON.stringify(data))));
}

export function unserializeGenericParams(enc: string, orgId = "") {
  const detyped = [..."escape"].join("");
  const escape = window[detyped as keyof typeof window] as (
    val: string // AlL tHiS tO gET TS To ShUT uP aBoUt `window.escape`
  ) => string;
  return JSON.parse(decodeURIComponent(escape(window.atob(enc))));
}

export function unserializeParams(enc: string, orgId = "") {
  // If we don't have an encoded string, return a default state
  if (!enc || enc.trim() === "") {
    return { orgId };
  }

  try {
    return ensureState(unserializeGenericParams(enc, orgId), orgId);
  } catch {
    return { orgId };
  }
}

export function ensureState(raw: any, orgId = ""): SearchState {
  const current = typeof raw === "object" ? { ...raw } : {};

  // sweet, sweet sugar. "#{{Issue}} xyz" => 'select Issue search for xyz'
  const search = typeof current["search"] === "string" ? current["search"] : "";
  const { searchTerm, entity } = askEntityExtraction(search);
  const select = Array.isArray(current["select"]) ? current["select"] : [];
  if (select.length === 0 && entity != null) {
    select.push(entity);
  }

  // note: SearchState only accepts aggregations array,
  // but previously serialized data may have a single 'aggregation' field instead.
  current["aggregations"] =
    current["aggregations"] ||
    [current["aggregation"]].filter((a) => a != undefined);

  const hasStartDate = typeof current["startDate"] === "string";
  const hasEndDate = typeof current["endDate"] === "string";
  const hasDuration = isDuration(current["duration"]);

  const duration =
    hasDuration && !hasStartDate && !hasEndDate
      ? current["duration"]
      : undefined;

  const groupBys = // bw compat
    current["groupBy"] && !current["groupBys"]
      ? (current["groupBys"] = current["groupBy"])
      : current["groupBys"];
  const candidate = {
    orgId: orgId || current["orgId"],
    select,
    filters: Array.isArray(current["filters"]) ? current["filters"] : [],
    search: searchTerm,
    startDate: hasStartDate ? current["startDate"] : undefined,
    endDate: hasEndDate ? current["endDate"] : undefined,
    duration,
    groupBys: Array.isArray(groupBys) ? groupBys : [],
    aggregations: current["aggregations"],
    compareWith: current["compareWith"],
    formulas: current["formulas"],
    having: current["having"],
  };
  if (candidate.compareWith?.ago) {
    candidate.compareWith.method = "tuple";
  }
  candidate.compareWith;
  candidate.groupBys = candidate.groupBys.map((f) =>
    typeof f === "string"
      ? {
          target: "Event",
          attribute: f,
        }
      : f
  );
  candidate.filters = candidate.filters.map((f) => ({
    ...f,
    target: f.target || "Event",
  }));

  return candidate;
}
